/* eslint max-lines: ["error", 800] */
import { useQuery } from '@apollo/client/react';
import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PaymentIcon from '@mui/icons-material/Payment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import PageContainer from '../../core/components/PageContainer';
import ResponsiveActionButton from '../../core/components/ResponsiveActionButton';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';
import useProperty from '../../property/hooks/useProperty';
import useMarkCleaningsAsPaid from '../hooks/markCleaningsAsPaid';
import { cleaningListQuery } from '../queries/cleaningQueries';
import CleaningForm from './CleaningForm';
import PaymentConfirmationDialog from './PaymentConfirmationDialog';

type CleaningRecord = {
    id: number;
    time: string;
    note: string;
    value: number;
    paid: boolean;
};

type MonthGroup = {
    monthKey: string;
    monthLabel: string;
    records: CleaningRecord[];
    total: number;
    unpaidRecords: CleaningRecord[];
};

const groupCleaningsByMonth = (cleanings: CleaningRecord[]): MonthGroup[] => {
    const grouped = new Map<string, CleaningRecord[]>();

    for (const cleaning of cleanings) {
        const dt = DateTime.fromSQL(cleaning.time);
        const monthKey = dt.toFormat('yyyy-MM');
        const existing = grouped.get(monthKey) ?? [];
        grouped.set(monthKey, [...existing, cleaning]);
    }

    const monthGroups: MonthGroup[] = [];
    for (const [monthKey, records] of Array.from(grouped.entries())) {
        const sortedRecords = [...records].sort((a, b) => {
            const dateA = DateTime.fromSQL(a.time);
            const dateB = DateTime.fromSQL(b.time);
            return dateA.toMillis() - dateB.toMillis();
        });
        const dt = DateTime.fromFormat(monthKey, 'yyyy-MM');
        monthGroups.push({
            monthKey,
            monthLabel: dt.toFormat('MMMM yyyy'),
            records: sortedRecords,
            total: sortedRecords.reduce((sum, r) => sum + r.value, 0),
            unpaidRecords: sortedRecords.filter(r => !r.paid),
        });
    }

    return monthGroups.sort((a, b) => a.monthKey.localeCompare(b.monthKey));
};

const moneyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'decimal',
    maximumFractionDigits: 0,
});

const Cleaning = () => {
    const propertyId = usePropertyId();
    const {
        isCleaningEnabled,
        property,
        loading: propertyLoading,
    } = useProperty();
    const propertyCurrency = property?.currency ?? 'EUR';
    const pathname = window.location.hash.replace('#', '');

    const isLegacyRoute = pathname === '/cleaning';
    const isPropertyRoute = /^\/\d+\/cleaning/.test(pathname);

    const [showCleaningForm, setShowCleaningForm] = useState(false);
    const [editingCleaning, setEditingCleaning] =
        useState<CleaningRecord | null>(null);
    const {
        data: { cleaningList = [] } = {
            cleaningList: [],
        },
        refetch,
        loading,
    } = useQuery<{
        cleaningList: CleaningRecord[];
    }>(cleaningListQuery, {
        variables: { propertyId: propertyId ?? 0 },
        skip: !propertyId,
    });

    const markCleaningsAsPaid = useMarkCleaningsAsPaid();

    const suggestedTime = `${DateTime.local().toSQLDate()} 12:00`;

    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [showPaymentConfirmation, setShowPaymentConfirmation] =
        useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);

    const monthGroups = useMemo(
        () =>
            groupCleaningsByMonth(cleaningList).filter(
                group => group.unpaidRecords.length > 0
            ),
        [cleaningList]
    );

    const today = useMemo(() => DateTime.local().startOf('day'), []);

    const unpaidToDateRecords = useMemo(() => {
        return cleaningList.filter(record => {
            if (record.paid) return false;
            const recordDate = DateTime.fromSQL(record.time).startOf('day');
            return recordDate <= today;
        });
    }, [cleaningList, today]);

    const unpaidToDateTotal = useMemo(() => {
        return unpaidToDateRecords.reduce((sum, r) => sum + r.value, 0);
    }, [unpaidToDateRecords]);

    const handleSelectAllToDate = useCallback(() => {
        const unpaidToDateIds = unpaidToDateRecords.map(r => r.id);
        const newSelected = new Set([...selectedItems, ...unpaidToDateIds]);
        setSelectedItems(Array.from(newSelected));
    }, [unpaidToDateRecords, selectedItems]);

    const getMonthCheckboxState = useCallback(
        (monthGroup: MonthGroup) => {
            const unpaidIds = monthGroup.unpaidRecords.map(r => r.id);
            if (unpaidIds.length === 0)
                return { checked: false, indeterminate: false, disabled: true };
            const selectedCount = unpaidIds.filter(id =>
                selectedItems.includes(id)
            ).length;
            return {
                checked: selectedCount === unpaidIds.length,
                indeterminate:
                    selectedCount > 0 && selectedCount < unpaidIds.length,
                disabled: false,
            };
        },
        [selectedItems]
    );

    const handleMonthCheckboxClick = useCallback(
        (monthGroup: MonthGroup, event: React.MouseEvent) => {
            event.stopPropagation();
            const unpaidIds = monthGroup.unpaidRecords.map(r => r.id);
            const allSelected = unpaidIds.every(id =>
                selectedItems.includes(id)
            );
            if (allSelected) {
                setSelectedItems(
                    selectedItems.filter(id => !unpaidIds.includes(id))
                );
            } else {
                const newSelected = new Set([...selectedItems, ...unpaidIds]);
                setSelectedItems(Array.from(newSelected));
            }
        },
        [selectedItems]
    );

    const checkboxRefs = useRef<Map<string, HTMLInputElement>>(new Map());

    const handleConfirmPayment = useCallback(() => {
        setPaymentLoading(true);
        markCleaningsAsPaid(selectedItems)
            .then(() => {
                refetch();
                setSelectedItems([]);
                setShowPaymentConfirmation(false);
            })
            .catch(error => {
                throw error;
            })
            .finally(() => {
                setPaymentLoading(false);
            });
    }, [selectedItems, markCleaningsAsPaid, refetch]);

    if (!propertyId) {
        if (isPropertyRoute) {
            return null;
        }
        if (isLegacyRoute) {
            return (
                <PropertySelectionDialog open={true} targetPath="/cleaning" />
            );
        }
        return null;
    }

    if (propertyLoading) {
        return (
            <PageContainer>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (!isCleaningEnabled()) {
        return (
            <PageContainer>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: '#9ca3af',
                        }}
                    >
                        Cleaning Module Not Enabled
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#6b7280',
                            textAlign: 'center',
                            maxWidth: 400,
                        }}
                    >
                        Enable the Cleaning Management module in property
                        settings to track cleaning records.
                    </Typography>
                </Box>
            </PageContainer>
        );
    }

    const selectedCleanings = cleaningList.filter(({ id }) =>
        selectedItems.includes(id)
    );

    const selectedTotalValue = selectedCleanings.reduce(
        (a, b) => a + b.value,
        0
    );

    const formatDateTime = (timeString: string) => {
        const dt = DateTime.fromSQL(timeString);
        return dt.toLocaleString(DateTime.DATETIME_MED);
    };

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 2, sm: 0 },
                    mb: 3,
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    Cleaning Records
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 2 },
                    }}
                >
                    {unpaidToDateRecords.length > 0 && (
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<ChecklistIcon />}
                            onClick={handleSelectAllToDate}
                            sx={{
                                borderRadius: 0,
                                border: '2px solid',
                                borderColor: 'secondary.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            Select All To Date
                        </Button>
                    )}
                    <ResponsiveActionButton
                        label="Add Cleaning"
                        icon={<AddIcon />}
                        onClick={() => {
                            setShowCleaningForm(true);
                        }}
                    />
                </Box>
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: '2px solid #374151',
                        borderRadius: 0,
                    }}
                >
                    <Table aria-label="cleaning records table">
                        <TableHead>
                            <TableRow
                                sx={{
                                    borderBottom: '3px solid #6366f1',
                                    backgroundColor: '#1a1a2e',
                                }}
                            >
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        py: 2,
                                        px: 2,
                                    }}
                                >
                                    Date & Time
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Note
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Value
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Select
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cleaningList.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        align="center"
                                        sx={{ py: 6 }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            No cleaning records found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                monthGroups.map(monthGroup => {
                                    const checkboxState =
                                        getMonthCheckboxState(monthGroup);
                                    return (
                                        <Fragment key={monthGroup.monthKey}>
                                            <TableRow
                                                data-testid="cleaning-month-row"
                                                sx={{
                                                    borderTop:
                                                        '3px solid #6366f1',
                                                    borderBottom:
                                                        '2px solid #374151',
                                                    backgroundColor:
                                                        'rgba(99, 102, 241, 0.08)',
                                                }}
                                            >
                                                <TableCell
                                                    colSpan={2}
                                                    sx={{ py: 1.5, px: 2 }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={800}
                                                        textTransform="uppercase"
                                                        letterSpacing="0.05em"
                                                        color="primary.main"
                                                    >
                                                        {monthGroup.monthLabel}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ px: 2 }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'column',
                                                            alignItems:
                                                                'flex-end',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight={700}
                                                            color="primary.main"
                                                        >
                                                            {moneyFormatter.format(
                                                                monthGroup.total
                                                            )}{' '}
                                                            {propertyCurrency}
                                                        </Typography>
                                                        {monthGroup
                                                            .unpaidRecords
                                                            .length > 0 && (
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={700}
                                                                color="warning.main"
                                                                sx={{
                                                                    fontSize:
                                                                        '0.75rem',
                                                                }}
                                                            >
                                                                {moneyFormatter.format(
                                                                    monthGroup.unpaidRecords.reduce(
                                                                        (
                                                                            sum,
                                                                            r
                                                                        ) =>
                                                                            sum +
                                                                            r.value,
                                                                        0
                                                                    )
                                                                )}{' '}
                                                                {
                                                                    propertyCurrency
                                                                }{' '}
                                                                unpaid
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ px: 2 }} />
                                                <TableCell
                                                    align="center"
                                                    sx={{ px: 2 }}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            checkboxState.checked
                                                        }
                                                        indeterminate={
                                                            checkboxState.indeterminate
                                                        }
                                                        disabled={
                                                            checkboxState.disabled
                                                        }
                                                        inputRef={(
                                                            el: HTMLInputElement | null
                                                        ) => {
                                                            if (el) {
                                                                checkboxRefs.current.set(
                                                                    monthGroup.monthKey,
                                                                    el
                                                                );
                                                            }
                                                        }}
                                                        onClick={event =>
                                                            handleMonthCheckboxClick(
                                                                monthGroup,
                                                                event
                                                            )
                                                        }
                                                        sx={{
                                                            '&.Mui-checked': {
                                                                color: 'primary.main',
                                                            },
                                                            '&.MuiCheckbox-indeterminate':
                                                                {
                                                                    color: 'primary.main',
                                                                },
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            {monthGroup.records.map(row => (
                                                <TableRow
                                                    key={row.id}
                                                    onClick={() =>
                                                        setEditingCleaning(row)
                                                    }
                                                    sx={{
                                                        borderBottom:
                                                            '1px solid #374151',
                                                        transition:
                                                            'all 0.2s ease',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(99, 102, 241, 0.1)',
                                                            transform:
                                                                'translateX(2px)',
                                                        },
                                                        opacity: row.paid
                                                            ? 0.5
                                                            : 1,
                                                    }}
                                                >
                                                    <TableCell
                                                        data-testid="cleaning-date-cell"
                                                        component="th"
                                                        scope="row"
                                                        sx={{ py: 2, px: 2 }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                        >
                                                            {formatDateTime(
                                                                row.time
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ px: 2 }}>
                                                        <Typography variant="body2">
                                                            {row.note}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        data-testid="cleaning-value-cell"
                                                        align="right"
                                                        sx={{ px: 2 }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight={700}
                                                        >
                                                            {moneyFormatter.format(
                                                                row.value
                                                            )}{' '}
                                                            {propertyCurrency}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{ px: 2 }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display:
                                                                    'inline-block',
                                                                px: 2,
                                                                py: 0.5,
                                                                border: '2px solid',
                                                                borderColor:
                                                                    row.paid
                                                                        ? 'success.main'
                                                                        : 'warning.main',
                                                                backgroundColor:
                                                                    row.paid
                                                                        ? 'rgba(34, 197, 94, 0.1)'
                                                                        : 'rgba(245, 158, 11, 0.1)',
                                                                fontWeight: 700,
                                                                fontSize:
                                                                    '0.75rem',
                                                                textTransform:
                                                                    'uppercase',
                                                                letterSpacing:
                                                                    '0.05em',
                                                                color: row.paid
                                                                    ? 'success.main'
                                                                    : 'warning.main',
                                                            }}
                                                        >
                                                            {row.paid
                                                                ? 'PAID'
                                                                : 'UNPAID'}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{ px: 2 }}
                                                    >
                                                        {!row.paid && (
                                                            <Checkbox
                                                                checked={selectedItems.includes(
                                                                    row.id
                                                                )}
                                                                onClick={event => {
                                                                    event.stopPropagation();
                                                                    if (
                                                                        selectedItems.includes(
                                                                            row.id
                                                                        )
                                                                    ) {
                                                                        setSelectedItems(
                                                                            selectedItems.filter(
                                                                                item =>
                                                                                    item !==
                                                                                    row.id
                                                                            )
                                                                        );
                                                                    } else {
                                                                        setSelectedItems(
                                                                            [
                                                                                ...selectedItems,
                                                                                row.id,
                                                                            ]
                                                                        );
                                                                    }
                                                                }}
                                                                sx={{
                                                                    '&.Mui-checked':
                                                                        {
                                                                            color: 'primary.main',
                                                                        },
                                                                }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                        <TableFooter>
                            <TableRow
                                sx={{
                                    borderTop: '3px solid #374151',
                                    backgroundColor: '#1a1a2e',
                                }}
                            >
                                <TableCell colSpan={2} sx={{ py: 2, px: 2 }}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={700}
                                        textTransform="uppercase"
                                        letterSpacing="0.05em"
                                    >
                                        Total (All Records)
                                    </Typography>
                                </TableCell>
                                <TableCell data-testid="cleaning-footer-value" align="right" sx={{ px: 2 }}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={700}
                                    >
                                        {moneyFormatter.format(
                                            monthGroups.reduce(
                                                (sum, group) =>
                                                    sum + group.total,
                                                0
                                            )
                                        )}{' '}
                                        {propertyCurrency}
                                    </Typography>
                                </TableCell>
                                <TableCell colSpan={2}></TableCell>
                            </TableRow>
                            <TableRow
                                sx={{
                                    borderTop: '2px solid #f59e0b',
                                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                }}
                            >
                                <TableCell colSpan={2} sx={{ py: 2, px: 2 }}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={700}
                                        textTransform="uppercase"
                                        letterSpacing="0.05em"
                                    >
                                        Unpaid Total
                                    </Typography>
                                </TableCell>
                                <TableCell data-testid="cleaning-footer-value" align="right" sx={{ px: 2 }}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={700}
                                        color="warning.main"
                                    >
                                        {moneyFormatter.format(
                                            monthGroups.reduce(
                                                (sum, group) =>
                                                    sum +
                                                    group.unpaidRecords.reduce(
                                                        (s, r) => s + r.value,
                                                        0
                                                    ),
                                                0
                                            )
                                        )}{' '}
                                        {propertyCurrency}
                                    </Typography>
                                </TableCell>
                                <TableCell colSpan={2}></TableCell>
                            </TableRow>
                            <TableRow
                                sx={{
                                    borderTop: '2px solid #f59e0b',
                                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                }}
                            >
                                <TableCell colSpan={2} sx={{ py: 2, px: 2 }}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={700}
                                        textTransform="uppercase"
                                        letterSpacing="0.05em"
                                    >
                                        Unpaid To Date
                                    </Typography>
                                </TableCell>
                                <TableCell data-testid="cleaning-footer-value" align="right" sx={{ px: 2 }}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={700}
                                        color="warning.main"
                                    >
                                        {moneyFormatter.format(
                                            unpaidToDateTotal
                                        )}{' '}
                                        {propertyCurrency}
                                    </Typography>
                                </TableCell>
                                <TableCell colSpan={2}></TableCell>
                            </TableRow>
                            {selectedTotalValue > 0 && (
                                <TableRow
                                    sx={{
                                        borderTop: '3px solid #6366f1',
                                        backgroundColor:
                                            'rgba(99, 102, 241, 0.15)',
                                    }}
                                >
                                    <TableCell
                                        colSpan={2}
                                        sx={{ py: 2, px: 2 }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight={700}
                                            color="primary"
                                            textTransform="uppercase"
                                            letterSpacing="0.05em"
                                        >
                                            Selected for Payment
                                        </Typography>
                                    </TableCell>
                                    <TableCell data-testid="cleaning-footer-value" align="right" sx={{ px: 2 }}>
                                        <Typography
                                            variant="body1"
                                            fontWeight={700}
                                            color="primary"
                                        >
                                            {moneyFormatter.format(
                                                selectedTotalValue
                                            )}{' '}
                                            {propertyCurrency}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        colSpan={2}
                                        align="right"
                                        sx={{ px: 2 }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<PaymentIcon />}
                                            onClick={() => {
                                                setShowPaymentConfirmation(
                                                    true
                                                );
                                            }}
                                        >
                                            Pay Selected
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}

            {showCleaningForm && (
                <CleaningForm
                    suggestedTime={suggestedTime}
                    onClose={() => {
                        setShowCleaningForm(false);
                    }}
                />
            )}

            {editingCleaning && (
                <CleaningForm
                    cleaning={{
                        ...editingCleaning,
                        id: String(editingCleaning.id),
                        time: editingCleaning.time.replace(' ', 'T'),
                    }}
                    onClose={() => {
                        setEditingCleaning(null);
                    }}
                />
            )}

            <PaymentConfirmationDialog
                open={showPaymentConfirmation}
                onClose={() => setShowPaymentConfirmation(false)}
                onConfirm={handleConfirmPayment}
                selectedCleanings={selectedCleanings}
                currency={propertyCurrency}
                loading={paymentLoading}
            />
        </PageContainer>
    );
};

export default Cleaning;
